/**
 * The side menu as icons only ("rail") or with its labels: each person's
 * choice, remembered per BROWSER (2026-10-30, laptop header). It is a
 * matter of screen size, not of who is signed in -- a front-desk PC that
 * wants the room keeps it for every shift -- so unlike
 * `user-preferences.ts` the key is not per user; it follows that store's
 * `nx:` key style.
 *
 * Only the desktop tier draws it (`SideNav.css`, >= 1024px): below that
 * the nav is already an icon rail (768-1023px) or a drawer (phone), and
 * the choice is kept, unused, for when the window is wide again.
 *
 * Every storage access is wrapped: a private window, blocked site data or
 * a full quota must leave the menu at its default, never take the shell
 * down.
 */
import { useCallback, useState } from 'react';

export const NAV_RAIL_STORAGE_KEY = 'nx:nav-rail';
const RAIL = 'icons';

export function loadNavRail(): boolean {
  try {
    return window.localStorage.getItem(NAV_RAIL_STORAGE_KEY) === RAIL;
  } catch {
    return false;
  }
}

export function saveNavRail(rail: boolean): void {
  try {
    // The default (labels) is the ABSENCE of the key, so clearing site
    // data and never having chosen read the same.
    if (rail) window.localStorage.setItem(NAV_RAIL_STORAGE_KEY, RAIL);
    else window.localStorage.removeItem(NAV_RAIL_STORAGE_KEY);
  } catch {
    // Not remembered; the choice still holds for this page.
  }
}

/** `[rail, setRail]`: the menu's state, read once and saved on change. */
export function useNavRail(): [boolean, (rail: boolean) => void] {
  const [rail, setRailState] = useState(loadNavRail);
  const setRail = useCallback((next: boolean) => {
    setRailState(next);
    saveNavRail(next);
  }, []);
  return [rail, setRail];
}
